import { c as _c } from "react/compiler-runtime";
import React, { createContext, type ReactNode, useCallback, useContext, useMemo } from "react";
import { categoryLabels } from "#/entities/category/model/categoryLabels";
import { difficultyLabels } from "#/entities/difficulty/model/difficultyLabels";
import { usePlaygroundViewParam } from "#/features/playground/hooks/usePlaygroundViewParam";
import type { ProjectCategory, ProjectDifficulty } from "#/server/db/generated/enums";
import { useMobileLayout, useSearchParam } from "#/shared/hooks";

/**
 * Helper functions for parsing/serializing URL query parameters.
 * These functions convert between URL-friendly string formats and typed values.
 * Exported for unit testing.
 */

/**
 * Parses a comma-separated string of category values into an array of ProjectCategory enum values.
 * Uses O(1) lookup via categoryLabels object for efficient validation.
 *
 * @param value - Comma-separated string of category values (e.g., "ARRAY,HEAP,STACK")
 * @returns Array of valid ProjectCategory enum values. Invalid values are filtered out.
 * @example
 * parseCategories("ARRAY,HEAP") // Returns [ProjectCategory.ARRAY, ProjectCategory.HEAP]
 * parseCategories("INVALID,ARRAY") // Returns [ProjectCategory.ARRAY]
 * parseCategories("") // Returns []
 */
export const parseCategories = (value: string): ProjectCategory[] => {
  if (!value) return [];
  return value.split(",").filter((cat): cat is ProjectCategory => cat in categoryLabels);
};

/**
 * Serializes an array of ProjectCategory enum values into a comma-separated string.
 *
 * @param categories - Array of ProjectCategory enum values
 * @returns Comma-separated string of category values, or empty string if array is empty
 * @example
 * serializeCategories([ProjectCategory.ARRAY, ProjectCategory.HEAP]) // Returns "ARRAY,HEAP"
 * serializeCategories([]) // Returns ""
 */
export const serializeCategories = (categories: ProjectCategory[]): string => {
  return categories.join(",");
};

/**
 * Parses a comma-separated string of difficulty values into an array of ProjectDifficulty enum values.
 * Uses O(1) lookup via difficultyLabels object for efficient validation.
 *
 * @param value - Comma-separated string of difficulty values (e.g., "EASY,MEDIUM")
 * @returns Array of valid ProjectDifficulty enum values. Invalid values are filtered out.
 * @example
 * parseDifficulties("EASY,MEDIUM") // Returns [ProjectDifficulty.EASY, ProjectDifficulty.MEDIUM]
 * parseDifficulties("INVALID,EASY") // Returns [ProjectDifficulty.EASY]
 * parseDifficulties("") // Returns []
 */
export const parseDifficulties = (value: string): ProjectDifficulty[] => {
  if (!value) return [];
  return value.split(",").filter((diff): diff is ProjectDifficulty => diff in difficultyLabels);
};

/**
 * Serializes an array of ProjectDifficulty enum values into a comma-separated string.
 *
 * @param difficulties - Array of ProjectDifficulty enum values
 * @returns Comma-separated string of difficulty values, or empty string if array is empty
 * @example
 * serializeDifficulties([ProjectDifficulty.EASY, ProjectDifficulty.MEDIUM]) // Returns "EASY,MEDIUM"
 * serializeDifficulties([]) // Returns ""
 */
export const serializeDifficulties = (difficulties: ProjectDifficulty[]): string => {
  return difficulties.join(",");
};

/**
 * Parses a string value into a boolean.
 * Accepts "true" or "1" as truthy values, everything else is false.
 *
 * @param value - String value to parse
 * @returns Boolean value
 * @example
 * parseBoolean("true") // Returns true
 * parseBoolean("1") // Returns true
 * parseBoolean("false") // Returns false
 * parseBoolean("") // Returns false
 */
export const parseBoolean = (value: string): boolean => {
  return value === "true" || value === "1";
};

/**
 * Serializes a boolean value into a string for URL query parameters.
 * Returns "true" for true, empty string for false (to keep URLs clean).
 *
 * @param value - Boolean value to serialize
 * @returns "true" if value is true, empty string if false
 * @example
 * serializeBoolean(true) // Returns "true"
 * serializeBoolean(false) // Returns ""
 */
export const serializeBoolean = (value: boolean): string => {
  return value ? "true" : "";
};

/**
 * Constants for sort validation and defaults.
 */
const VALID_SORT_BY = ["title", "difficulty", "date", "category"] as const;
type ValidSortBy = (typeof VALID_SORT_BY)[number];
type ValidSortOrder = "asc" | "desc";
const DEFAULT_SORT_BY: ValidSortBy = "category";
const DEFAULT_SORT_ORDER: ValidSortOrder = "asc";
const DEFAULT_SORT = `${DEFAULT_SORT_BY}${DEFAULT_SORT_ORDER.charAt(0).toUpperCase() + DEFAULT_SORT_ORDER.slice(1)}`; // "categoryAsc"

/**
 * Parses a combined sort parameter string into separate sortBy and sortOrder values.
 * The format is: {sortBy}{Order} where Order is capitalized (e.g., "titleAsc", "difficultyDesc").
 * Case-insensitive matching is supported.
 *
 * @param value - Combined sort string (e.g., "titleAsc", "difficultyDesc", "TITLEASC")
 * @returns Object with sortBy and sortOrder properties. Returns defaults if value is invalid or empty.
 * @example
 * parseSort("titleAsc") // Returns { sortBy: "title", sortOrder: "asc" }
 * parseSort("difficultyDesc") // Returns { sortBy: "difficulty", sortOrder: "desc" }
 * parseSort("invalid") // Returns { sortBy: "category", sortOrder: "asc" } (defaults)
 * parseSort("") // Returns { sortBy: "category", sortOrder: "asc" } (defaults)
 */
export const parseSort = (value: string): {
  sortBy: ValidSortBy;
  sortOrder: ValidSortOrder;
} => {
  if (!value) {
    return {
      sortBy: DEFAULT_SORT_BY,
      sortOrder: DEFAULT_SORT_ORDER
    };
  }

  // Match pattern: {sortBy}{Order} where Order is Asc or Desc (case insensitive)
  // e.g., "titleAsc", "difficultyDesc", "categoryAsc", "TITLEASC"
  const match = value.match(/^(title|difficulty|date|category)(asc|desc)$/i);
  if (match) {
    const [, sortBy, order] = match;
    if (sortBy && order) {
      const normalizedSortBy = sortBy.toLowerCase();
      const normalizedOrder = order.toLowerCase();
      if (VALID_SORT_BY.includes(normalizedSortBy as (typeof VALID_SORT_BY)[number]) && (normalizedOrder === "asc" || normalizedOrder === "desc")) {
        return {
          sortBy: normalizedSortBy as ValidSortBy,
          sortOrder: normalizedOrder as ValidSortOrder
        };
      }
    }
  }
  return {
    sortBy: DEFAULT_SORT_BY,
    sortOrder: DEFAULT_SORT_ORDER
  };
};

/**
 * Serializes separate sortBy and sortOrder values into a combined sort parameter string.
 * The format is: {sortBy}{Order} where Order is capitalized (e.g., "titleAsc", "difficultyDesc").
 *
 * @param sortBy - Sort field ("title", "difficulty", "date", or "category")
 * @param sortOrder - Sort order ("asc" or "desc")
 * @returns Combined sort string (e.g., "titleAsc", "difficultyDesc")
 * @example
 * serializeSort("title", "asc") // Returns "titleAsc"
 * serializeSort("difficulty", "desc") // Returns "difficultyDesc"
 */
export const serializeSort = (sortBy: ValidSortBy, sortOrder: ValidSortOrder): string => {
  const orderCapitalized = sortOrder.charAt(0).toUpperCase() + sortOrder.slice(1);
  return `${sortBy}${orderCapitalized}`;
};

/**
 * Context value type for ProjectBrowser state management.
 * All state is synchronized with URL query parameters for shareable URLs.
 */
type ProjectBrowserContextValue = {
  /** Opens the project browser drawer by setting URL param `browser=true` */
  openBrowser: () => void;
  /** Closes the project browser drawer by clearing URL param `browser` */
  closeBrowser: () => void;
  /** Whether the browser drawer is currently open (read from URL param `browser`) */
  isOpen: boolean;

  /** Current search query string (read from URL param `search`) */
  searchQuery: string;
  /** Updates the search query URL parameter */
  setSearchQuery: (query: string) => void;

  /** Currently selected categories (read from URL param `categories`, comma-separated) */
  selectedCategories: ProjectCategory[];
  /** Updates the categories URL parameter */
  setSelectedCategories: (categories: ProjectCategory[]) => void;

  /** Currently selected difficulties (read from URL param `difficulties`, comma-separated) */
  selectedDifficulties: ProjectDifficulty[];
  /** Updates the difficulties URL parameter */
  setSelectedDifficulties: (difficulties: ProjectDifficulty[]) => void;

  /** Whether to show only new projects (read from URL param `new`) */
  showOnlyNew: boolean;
  /** Updates the showOnlyNew URL parameter */
  setShowOnlyNew: (show: boolean) => void;

  /**
   * Current sort field (parsed from URL param `sort`, e.g., "titleAsc" → "title")
   * Combined with sortOrder for a single URL parameter
   */
  sortBy: ValidSortBy;
  /**
   * Current sort order (parsed from URL param `sort`, e.g., "titleAsc" → "asc")
   * Combined with sortBy for a single URL parameter
   */
  sortOrder: ValidSortOrder;
  /**
   * Updates the sort URL parameter with a combined value.
   * @param sort - Combined sort string (e.g., "titleAsc", "difficultyDesc")
   */
  setSort: (sort: string) => void;

  /** Resets all filter URL parameters to empty/default values */
  resetFilters: () => void;
};

/**
 * React Context for ProjectBrowser state management.
 * Provides URL-synchronized state for browser open/close, filters, and sorting.
 */
const ProjectBrowserContext = createContext<ProjectBrowserContextValue | null>(null);

/**
 * Hook to access ProjectBrowser context values.
 * Must be used within a ProjectBrowserProvider.
 *
 * @returns ProjectBrowserContextValue with all state and setters
 * @throws Error if used outside ProjectBrowserProvider
 * @example
 * const { isOpen, openBrowser, searchQuery, setSearchQuery } = useProjectBrowserContext();
 */
export const useProjectBrowserContext = () => {
  const context = useOptionalProjectBrowserContext();
  if (!context) {
    throw new Error("useProjectBrowserContext must be used within ProjectBrowserProvider");
  }
  return context;
};

/** Returns null outside playground {@link ProjectBrowserProvider}. */
export const useOptionalProjectBrowserContext = () => {
  return useContext(ProjectBrowserContext);
};
type ProjectBrowserProviderProps = {
  children: ReactNode;
};

/**
 * Provider component that manages ProjectBrowser state synchronized with URL query parameters.
 * All state changes update the URL, enabling shareable filtered project lists.
 *
 * URL Parameters:
 * - `browser` - Controls drawer open/close (boolean: "true" or empty)
 * - `search` - Search query string
 * - `categories` - Comma-separated category values (e.g., "ARRAY,HEAP")
 * - `difficulties` - Comma-separated difficulty values (e.g., "EASY,MEDIUM")
 * - `new` - Show only new projects flag (boolean: "true" or empty)
 * - `sort` - Combined sort value (e.g., "titleAsc", "difficultyDesc")
 *
 * @param props - Component props
 * @param props.children - Child components that can access the context
 *
 * @example
 * ```tsx
 * <ProjectBrowserProvider>
 *   <App />
 * </ProjectBrowserProvider>
 * ```
 */
export const ProjectBrowserProvider: React.FC<ProjectBrowserProviderProps> = t0 => {
  const $ = _c(49);
  const {
    children
  } = t0;
  const isMobile = useMobileLayout();
  const {
    view,
    setView
  } = usePlaygroundViewParam();
  const isOpen = !isMobile && view === "browse";
  const [searchParam, setSearchParam] = useSearchParam("search");
  const [categoriesParam, setCategoriesParam] = useSearchParam("categories");
  const [difficultiesParam, setDifficultiesParam] = useSearchParam("difficulties");
  const [newParam, setNewParam] = useSearchParam("new");
  const [sortParam, setSortParam] = useSearchParam("sort");
  let t1;
  if ($[0] !== setView) {
    t1 = newState => {
      setView(newState ? "browse" : "");
    };
    $[0] = setView;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const setOpened = t1;
  let t2;
  if ($[2] !== categoriesParam) {
    t2 = parseCategories(categoriesParam);
    $[2] = categoriesParam;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const selectedCategories = t2;
  let t3;
  if ($[4] !== difficultiesParam) {
    t3 = parseDifficulties(difficultiesParam);
    $[4] = difficultiesParam;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const selectedDifficulties = t3;
  let t4;
  if ($[6] !== newParam) {
    t4 = parseBoolean(newParam);
    $[6] = newParam;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const showOnlyNew = t4;
  let t5;
  if ($[8] !== sortParam) {
    t5 = parseSort(sortParam);
    $[8] = sortParam;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const {
    sortBy,
    sortOrder
  } = t5;
  let t6;
  if ($[10] !== setOpened) {
    t6 = () => {
      setOpened(true);
    };
    $[10] = setOpened;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const openBrowser = t6;
  let t7;
  if ($[12] !== setOpened) {
    t7 = () => {
      setOpened(false);
    };
    $[12] = setOpened;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const closeBrowser = t7;
  let t8;
  if ($[14] !== setSearchParam) {
    t8 = query => {
      setSearchParam(query || "");
    };
    $[14] = setSearchParam;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const setSearchQuery = t8;
  let t9;
  if ($[16] !== setCategoriesParam) {
    t9 = categories => {
      setCategoriesParam(serializeCategories(categories));
    };
    $[16] = setCategoriesParam;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const setSelectedCategories = t9;
  let t10;
  if ($[18] !== setDifficultiesParam) {
    t10 = difficulties => {
      setDifficultiesParam(serializeDifficulties(difficulties));
    };
    $[18] = setDifficultiesParam;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const setSelectedDifficulties = t10;
  let t11;
  if ($[20] !== setNewParam) {
    t11 = show => {
      setNewParam(serializeBoolean(show));
    };
    $[20] = setNewParam;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const setShowOnlyNew = t11;
  let t12;
  if ($[22] !== setSortParam) {
    t12 = sort => {
      const parsed = parseSort(sort);
      const serialized = serializeSort(parsed.sortBy, parsed.sortOrder);
      if (serialized === DEFAULT_SORT) {
        setSortParam("");
      } else {
        setSortParam(serialized);
      }
    };
    $[22] = setSortParam;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const setSort = t12;
  let t13;
  if ($[24] !== setCategoriesParam || $[25] !== setDifficultiesParam || $[26] !== setNewParam || $[27] !== setSearchParam || $[28] !== setSortParam) {
    t13 = () => {
      setSearchParam("");
      setCategoriesParam("");
      setDifficultiesParam("");
      setNewParam("");
      setSortParam("");
    };
    $[24] = setCategoriesParam;
    $[25] = setDifficultiesParam;
    $[26] = setNewParam;
    $[27] = setSearchParam;
    $[28] = setSortParam;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const resetFilters = t13;
  const t14 = searchParam || "";
  let t15;
  if ($[30] !== closeBrowser || $[31] !== isOpen || $[32] !== openBrowser || $[33] !== resetFilters || $[34] !== selectedCategories || $[35] !== selectedDifficulties || $[36] !== setSearchQuery || $[37] !== setSelectedCategories || $[38] !== setSelectedDifficulties || $[39] !== setShowOnlyNew || $[40] !== setSort || $[41] !== showOnlyNew || $[42] !== sortBy || $[43] !== sortOrder || $[44] !== t14) {
    t15 = {
      openBrowser,
      closeBrowser,
      isOpen,
      searchQuery: t14,
      setSearchQuery,
      selectedCategories,
      setSelectedCategories,
      selectedDifficulties,
      setSelectedDifficulties,
      showOnlyNew,
      setShowOnlyNew,
      sortBy,
      sortOrder,
      setSort,
      resetFilters
    };
    $[30] = closeBrowser;
    $[31] = isOpen;
    $[32] = openBrowser;
    $[33] = resetFilters;
    $[34] = selectedCategories;
    $[35] = selectedDifficulties;
    $[36] = setSearchQuery;
    $[37] = setSelectedCategories;
    $[38] = setSelectedDifficulties;
    $[39] = setShowOnlyNew;
    $[40] = setSort;
    $[41] = showOnlyNew;
    $[42] = sortBy;
    $[43] = sortOrder;
    $[44] = t14;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  const value = t15;
  let t16;
  if ($[46] !== children || $[47] !== value) {
    t16 = <ProjectBrowserContext.Provider value={value}>{children}</ProjectBrowserContext.Provider>;
    $[46] = children;
    $[47] = value;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  return t16;
};